// src/store/useFileStore.js
// ✅ Business Logic Layer - Pinia Store
// - DB 레이어(fileManagerDB.js)를 통한 I/O
// - 상태 관리, 캐싱, Worker 통신
// - UI 변환 로직
// ✅ 수정: 3D 메타데이터 필드 명시, cancelled Set 정리, getter 추가

import { ref, computed } from 'vue';
import { defineStore } from 'pinia';
import { logInfo, logWarn, logError, logDebug } from '@/utils/logHelper';
import { getMimeType } from '@/utils/mimeTypes';
import { generateUUID, createBlobUrl, revokeBlobUrl } from '@/utils/blobHelpers';
import {
  getDB,
  getAllPaged,
  getFile,
  addFileStub,
  patchFile,
  deleteFile,
  deleteReviewTicketsByFileUuid,
} from '@/db/fileManagerDB';
import { uploadLimiter } from '@/utils/concurrency';
import useModelFileLoader from '@/composables/useModelFileLoader';

/* ═══════════════════════════════════════════════════════════
 * Web Worker 초기화 (Lazy)
 * ═══════════════════════════════════════════════════════════ */
let modelWorker = null;

function ensureWorker() {
  if (!modelWorker) {
    modelWorker = new Worker(
      new URL('@/workers/modelWorker.js', import.meta.url),
      { type: 'module' }
    );
  }
  return modelWorker;
}

/* ═══════════════════════════════════════════════════════════
 * Pinia Store Definition
 * ═══════════════════════════════════════════════════════════ */
export const useFileStore = defineStore('file', () => {
  /* ─────────────────────────────────────────────────────────
   * 1) State
   * ───────────────────────────────────────────────────────── */
  const files = ref([]);
  const page = ref(0);
  const pageSize = 100;

  // 업로드/변환 취소 제어
  const abortControllers = new Map(); // id → AbortController
  const cancelled = new Set();        // 삭제/취소된 id

  // count 업데이트 디바운스
  const pendingCounts = new Map();
  let countFlushTimer = null;

  // ✅ 추가: cancelled 정리 타이머
  const CANCELLED_CLEANUP_DELAY = 60000; // 1분 후 정리

  /* ─────────────────────────────────────────────────────────
   * 2) Getters (Computed)
   * ───────────────────────────────────────────────────────── */
  const totalCount = computed(() => files.value.length);
  
  const uploading = computed(() =>
    files.value.filter(f => f.status === 'processing' || f.status === 'queued')
  );

  const completedFiles = computed(() =>
    files.value.filter(f => f.status === 'completed' && f.success)
  );

  const errorFiles = computed(() =>
    files.value.filter(f => f.status === 'error')
  );

  // ✅ 추가: 견적용 BoundingBox 데이터 (API 전송 형식)
  const boundingBoxDataForApi = computed(() =>
    completedFiles.value.map((file, idx) => ({
      fileId: file.id,
      fileName: file.name,
      fileUUID: file.uuid,
      fileSize: file.size,
      fileType: file.type,
      fileCounts: file.count ?? 1,
      rawUrl: file.rawUrl || null,
      glbUrl: file.glbUrl || null,
      thumbnailUrl: file.thumbnailUrl || null,
      volume: file.volume ?? null,
      surfaceArea: file.surfaceArea ?? null,
      edgeLength: file.edgeLength ?? null,
      centerOfMass: file.centerOfMass || null,
      mass: file.mass ?? null,
      boundingBoxMm: file.boundingBoxMm || null,
    }))
  );

  // ✅ 추가: 완료된 파일 존재 여부
  const hasCompletedFiles = computed(() => completedFiles.value.length > 0);

  // ✅ 추가: 처리 중인 파일 존재 여부
  const isProcessing = computed(() => uploading.value.length > 0);

  /* ─────────────────────────────────────────────────────────
   * 3) Internal Helpers
   * ───────────────────────────────────────────────────────── */
  
  /**
   * ✅ 수정: DB 레코드 → UI용 객체 변환 (3D 메타데이터 명시)
   */
  function toViewModel(record) {
    if (!record) return null;
    
    return {
      // 기본 필드
      id: record.id,
      uuid: record.uuid,
      name: record.name,
      size: record.size,
      type: record.type,
      count: record.count ?? 1,
      status: record.status,
      success: record.success ?? false,
      errorMessage: record.errorMessage || null,
      lastModified: record.lastModified,
      lastModifiedDate: record.lastModifiedDate,
      
      // ✅ 3D 메타데이터 (loadModel 결과) - 명시적 정의
      rawUrl: record.rawUrl || null,
      glbUrl: record.glbUrl || null,
      thumbnailUrl: record.thumbnailUrl || null,
      volume: record.volume ?? null,
      surfaceArea: record.surfaceArea ?? null,
      edgeLength: record.edgeLength ?? null,
      centerOfMass: record.centerOfMass || null,
      mass: record.mass ?? null,
      boundingBoxMm: record.boundingBoxMm || null,
      
      // ✅ 추가: 추가 3D 속성 (필요시)
      dimensions: record.dimensions || null,
      triangleCount: record.triangleCount ?? null,
      vertexCount: record.vertexCount ?? null,
      
      // UI 전용 필드
      displayName: record.name || 'Unnamed',
      statusLabel: getStatusLabel(record.status),
      isCompleted: record.status === 'completed' && record.success,
      isError: record.status === 'error',
      isProcessing: record.status === 'processing' || record.status === 'queued',
    };
  }

  function getStatusLabel(status) {
    const labels = {
      queued: '대기중',
      processing: '처리중',
      completed: '완료',
      error: '오류',
    };
    return labels[status] || status;
  }

  /**
   * AbortController 생성
   */
  function createAbortFor(id) {
    const controller = new AbortController();
    abortControllers.set(id, controller);
    return controller;
  }

  /**
   * 업로드/변환 취소
   */
  function cancelAbort(id, reason = 'USER_CANCELED') {
    const controller = abortControllers.get(id);
    if (controller) {
      controller.abort(reason);
      abortControllers.delete(id);
    }
  }

  /**
   * ✅ 추가: cancelled Set 정리 (메모리 누수 방지)
   */
  function scheduleCancelledCleanup(id) {
    setTimeout(() => {
      cancelled.delete(id);
      logDebug(`[useFileStore] Cleaned up cancelled ID: ${id}`);
    }, CANCELLED_CLEANUP_DELAY);
  }

  /**
   * 워커 메시지 구독
   */
  function subscribeWorker() {
    const worker = ensureWorker();
    
    worker.onmessage = async ({ data }) => {
      const { id, status, message } = data;
      
      // 삭제/취소된 항목의 워커 결과는 무시
      if (cancelled.has(id)) return;

      if (status === 'completed') {
        await updateFileAction(id, { status: 'completed', success: true });
      } else {
        await updateFileAction(id, { 
          status: 'error', 
          success: false, 
          errorMessage: message 
        });
      }
    };
  }

  // 초기화 시 워커 구독
  subscribeWorker();

  /* ─────────────────────────────────────────────────────────
   * 4) Actions
   * ───────────────────────────────────────────────────────── */

  /**
   * 파일 목록 새로고침
   */
  async function refreshAction() {
    try {
      const records = await getAllPaged({ 
        limit: pageSize, 
        offset: page.value * pageSize 
      });
      files.value = records.map(toViewModel);
      logDebug(`[useFileStore.refresh] Loaded ${files.value.length} files`);
    } catch (err) {
      logError('[useFileStore.refresh] Failed', err);
      throw err;
    }
  }

  /**
   * 단일 파일 로드
   */
  async function loadFileAction(id) {
    try {
      const record = await getFile(id);
      return toViewModel(record);
    } catch (err) {
      logError(`[useFileStore.loadFile] ID=${id} failed`, err);
      throw err;
    }
  }

  /**
   * 파일 업데이트
   */
  async function updateFileAction(id, patch) {
    // 삭제/취소된 항목은 무시
    if (cancelled.has(id)) return null;

    try {
      const updated = await patchFile(id, patch, { silentIfMissing: true });
      
      if (!updated) return null;

      // 로컬 상태 업데이트
      const idx = files.value.findIndex(f => f.id === id);
      if (idx > -1) {
        files.value[idx] = toViewModel(updated);
      }

      return files.value[idx] || toViewModel(updated);
    } catch (err) {
      // 업로드 중 삭제된 경우 조용히 무시
      logDebug(`[useFileStore.updateFile] ID=${id} 업데이트 실패 (삭제됨?)`, err);
      return null;
    }
  }

  /**
   * 파일 추가 + 변환
   * @param {File} rawFile - 업로드할 File 객체
   * @param {Object} callbacks - { onQueued, onSuccess, onError }
   */
  async function addFileAction(rawFile, { onQueued, onSuccess, onError } = {}) {
    try {
      // 1) DB에 스텁 저장
      const stub = {
        uuid: generateUUID(),
        name: rawFile.name,
        size: rawFile.size,
        type: rawFile.type || getMimeType(rawFile.name),
        lastModified: rawFile.lastModified,
        lastModifiedDate: new Date(rawFile.lastModified).toLocaleString(),
        count: 1,
        status: 'queued',
        success: false,
      };

      const id = await addFileStub(stub);
      const persisted = toViewModel({ ...stub, id });
      
      // 로컬 상태에 추가
      files.value.push(persisted);
      onQueued?.(persisted);
      logInfo('[useFileStore.addFile] File queued', persisted.name);

      // 2) 상태를 processing으로 변경
      await updateFileAction(id, { status: 'processing' });

      // 3) 변환 실행 (동시성 제어)
      const controller = createAbortFor(id);
      
      uploadLimiter(() => {
        // 대기 중에 삭제되었을 수 있음
        if (cancelled.has(id)) {
          abortControllers.delete(id);
          return;
        }

        const { loadModel } = useModelFileLoader();

        loadModel(rawFile, id, { signal: controller.signal })
          .then(async (parsed) => {
            if (cancelled.has(id)) return;
            
            const result = await updateFileAction(id, {
              status: 'completed',
              success: true,
              ...parsed,
            });
            
            onSuccess?.(result);
            logInfo('[useFileStore.addFile] File processed', result?.name);
          })
          .catch(async (err) => {
            // Abort 또는 사용자 취소는 무음 처리
            if (
              err?.name === 'AbortError' || 
              err?.code === 'ERR_CANCELED' || 
              err?.message === 'USER_CANCELED'
            ) {
              return;
            }
            
            if (cancelled.has(id)) return;

            const result = await updateFileAction(id, {
              status: 'error',
              success: false,
              errorMessage: err.message,
            });
            
            onError?.(err, result);
            logError('[useFileStore.addFile] Processing failed', err);
          })
          .finally(() => {
            abortControllers.delete(id);
          });
      });

      return persisted;
    } catch (err) {
      onError?.(err);
      logError('[useFileStore.addFile] Error', err);
      throw err;
    }
  }

  /**
   * URL 기반 파일 등록
   */
  async function addFileByUrl({ url, originalFileName, thumbnailUrl, glbUrl }) {
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      const mimeType = blob.type || getMimeType(originalFileName);
      const file = new File([blob], originalFileName, { type: mimeType });

      return await addFileAction(file, {});
    } catch (err) {
      logError('[useFileStore.addFileByUrl] Error', err);
      throw err;
    }
  }

  /**
   * ✅ 수정: 파일 삭제 (cancelled 정리 추가)
   */
  async function deleteFileAction(id) {
    try {
      // 1) 취소 처리
      cancelled.add(id);
      cancelAbort(id);
      
      // ✅ 추가: 일정 시간 후 cancelled Set에서 제거
      scheduleCancelledCleanup(id);
      
      // 워커에 취소 알림
      ensureWorker().postMessage?.({ type: 'cancel', id });

      // 2) 관련 리뷰 티켓 삭제
      const file = files.value.find(f => f.id === id);
      if (file?.uuid) {
        await deleteReviewTicketsByFileUuid(file.uuid);
      }

      // 3) DB에서 파일 삭제
      await deleteFile(id);

      // 4) 로컬 상태에서 제거
      files.value = files.value.filter(f => f.id !== id);
      
      logInfo(`[useFileStore.deleteFile] ID=${id} deleted`);
    } catch (err) {
      logError(`[useFileStore.deleteFile] ID=${id} failed`, err);
      throw err;
    }
  }

  /**
   * 여러 파일 업로드
   */
  async function uploadFilesAction(fileList, callbacks) {
    return Promise.all(
      [...fileList].map(f => uploadLimiter(() => addFileAction(f, callbacks)))
    );
  }

  /**
   * ✅ 추가: 모든 파일 삭제
   */
  async function clearAllFilesAction() {
    const ids = files.value.map(f => f.id);
    await Promise.all(ids.map(id => deleteFileAction(id)));
    logInfo('[useFileStore.clearAllFiles] All files cleared');
  }

  /**
   * ✅ 추가: UUID로 파일 찾기
   */
  function getFileByUuid(uuid) {
    return files.value.find(f => f.uuid === uuid) || null;
  }

  /**
   * ✅ 추가: ID로 파일 찾기
   */
  function getFileById(id) {
    return files.value.find(f => f.id === id) || null;
  }

  /* ─────────────────────────────────────────────────────────
   * 5) Count Actions (✅ 수정: 즉시 저장 옵션 추가)
   * ───────────────────────────────────────────────────────── */
  
  /**
   * ✅ 수정: 외부에서 호출 가능하도록 async로 변경
   * 페이지 이동 전 반드시 호출 필요
   */
  async function flushPendingCounts() {
    if (countFlushTimer) {
      clearTimeout(countFlushTimer);
      countFlushTimer = null;
    }
    
    if (pendingCounts.size === 0) return;
    
    const entries = [...pendingCounts.entries()];
    pendingCounts.clear();
    
    for (const [id, count] of entries) {
      try {
        await patchFile(id, { count }, { silentIfMissing: true });
        logDebug(`[useFileStore] Count flushed: id=${id}, count=${count}`);
      } catch (err) {
        logError(`[useFileStore] Count flush failed: id=${id}`, err);
      }
    }
  }

  function scheduleCountFlush() {
    if (countFlushTimer) {
      clearTimeout(countFlushTimer);
    }
    countFlushTimer = setTimeout(flushPendingCounts, 500);
  }

  function incrementFileCountAction(id) {
    const file = files.value.find(f => f.id === id);
    if (!file) return;
    
    file.count = (file.count || 0) + 1;
    pendingCounts.set(id, file.count);
    scheduleCountFlush();
  }

  function decrementFileCountAction(id) {
    const file = files.value.find(f => f.id === id);
    if (!file) return;
    
    file.count = Math.max(0, (file.count || 0) - 1);
    pendingCounts.set(id, file.count);
    scheduleCountFlush();
  }

  /**
   * ✅ 수정: immediate 옵션 추가 - 즉시 DB 저장
   * @param {number} id - 파일 ID
   * @param {number} count - 수량
   * @param {Object} options - { immediate: boolean }
   */
  async function setFileCountAction(id, count, { immediate = false } = {}) {
    const file = files.value.find(f => f.id === id);
    if (!file) return;
    
    const newCount = Math.max(0, count);
    file.count = newCount;
    
    if (immediate) {
      // ✅ 즉시 DB 저장 (페이지 이동 전 등)
      try {
        await patchFile(id, { count: newCount }, { silentIfMissing: true });
        logDebug(`[useFileStore] Count saved immediately: id=${id}, count=${newCount}`);
      } catch (err) {
        logError(`[useFileStore] Immediate count save failed: id=${id}`, err);
      }
    } else {
      // 디바운스 저장
      pendingCounts.set(id, newCount);
      scheduleCountFlush();
    }
  }

  /**
   * ✅ 추가: 특정 파일의 pending count 즉시 저장
   */
  async function flushFileCount(id) {
    const count = pendingCounts.get(id);
    if (count === undefined) return;
    
    pendingCounts.delete(id);
    
    try {
      await patchFile(id, { count }, { silentIfMissing: true });
      logDebug(`[useFileStore] Single count flushed: id=${id}, count=${count}`);
    } catch (err) {
      logError(`[useFileStore] Single count flush failed: id=${id}`, err);
    }
  }

  /**
   * ✅ 추가: pending 여부 확인
   */
  function hasPendingCounts() {
    return pendingCounts.size > 0;
  }

  /* ─────────────────────────────────────────────────────────
   * 6) Expose
   * ───────────────────────────────────────────────────────── */
  return {
    // State
    files,
    page,
    pageSize,

    // Getters
    totalCount,
    uploading,
    completedFiles,
    errorFiles,
    boundingBoxDataForApi,  // ✅ 추가
    hasCompletedFiles,      // ✅ 추가
    isProcessing,           // ✅ 추가

    // Actions
    refreshAction,
    loadFileAction,
    updateFileAction,
    addFileAction,
    addFileByUrl,
    deleteFileAction,
    uploadFilesAction,
    clearAllFilesAction,    // ✅ 추가

    // Lookup
    getFileByUuid,          // ✅ 추가
    getFileById,            // ✅ 추가

    // Count Actions
    incrementFileCountAction,
    decrementFileCountAction,
    setFileCountAction,
    flushPendingCounts,    // ✅ 추가: 페이지 이동 전 호출용
    flushFileCount,        // ✅ 추가: 단일 파일 즉시 저장
    hasPendingCounts,      // ✅ 추가: pending 여부 확인

    // Alias
    loadFilesAction: refreshAction,
  };
});